import type { AdvisorDrawerContextKind } from "./advisor-context";
import type { AdvisorContext } from "./dashboard";

/**
 * Ask Sill drawer analytics (plan 435 M6, task asd-analytics). Allowlist-only
 * event names + typed capture helpers — mirrors the `lib/home-analytics.ts` /
 * `lib/marketplace-analytics.ts` idiom (ADR-030 D13): enum props only, never
 * an offer/agent id, a prompt, or message text.
 */

export type AdvisorCapture = (
  event: string,
  properties?: Record<string, unknown>,
) => void;

/** Every surface that can open the drawer (roadmap task asd-entry-repoint).
 * `deep_link` is the `?ask=1` query-flag entry point (review fix, iteration
 * 2, finding 3) — `ConsumerFrame`'s `useAskQueryParamOpensDrawer`. */
export type AdvisorDrawerSource =
  | "header"
  | "sidebar"
  | "cta"
  | "shortcut"
  | "deep_link";

/**
 * `context_kind` is the enum only — never the id a drawer context carries.
 * Admits BOTH context systems' kinds (`lib/advisor-context.ts`'s
 * `advisorContextKindOf` resolves either one to this type) since Expand can
 * surface a legacy `?context=` enum value just as easily as a drawer one.
 */
export type AdvisorAnalyticsContextKind =
  | AdvisorDrawerContextKind
  | Exclude<AdvisorContext, null>
  | "none";

export type AdvisorDrawerDurationBucket =
  | "under_1m"
  | "1_5m"
  | "5_15m"
  | "15m_plus";

export const ADVISOR_DRAWER_ANALYTICS_EVENTS = {
  opened: "advisor_drawer_opened",
  expanded: "advisor_drawer_expanded",
  closed: "advisor_drawer_closed",
} as const;

export function captureAdvisorDrawerOpened(
  capture: AdvisorCapture,
  source: AdvisorDrawerSource,
  contextKind: AdvisorAnalyticsContextKind,
) {
  capture(ADVISOR_DRAWER_ANALYTICS_EVENTS.opened, {
    source,
    context_kind: contextKind,
  });
}

export function captureAdvisorDrawerExpanded(
  capture: AdvisorCapture,
  contextKind: AdvisorAnalyticsContextKind,
  turns: number,
) {
  capture(ADVISOR_DRAWER_ANALYTICS_EVENTS.expanded, {
    context_kind: contextKind,
    turns,
  });
}

export function captureAdvisorDrawerClosed(
  capture: AdvisorCapture,
  turns: number,
  durationBucket: AdvisorDrawerDurationBucket,
) {
  capture(ADVISOR_DRAWER_ANALYTICS_EVENTS.closed, {
    turns,
    duration_bucket: durationBucket,
  });
}

/** Fixed buckets — never a raw millisecond/second value (ADR-030 D13). */
export function bucketAdvisorDrawerDuration(
  openedAtMs: number,
  closedAtMs: number,
): AdvisorDrawerDurationBucket {
  const elapsedMs = Math.max(0, closedAtMs - openedAtMs);
  const minutes = elapsedMs / 60_000;
  if (minutes < 1) return "under_1m";
  if (minutes < 5) return "1_5m";
  if (minutes < 15) return "5_15m";
  return "15m_plus";
}
